import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram, getEJobTypeSelectableList } from '@if/core/types'
import {
  ControlledNumberField,
  ControlledPhoneField,
  ControlledSelect,
  ControlledTextField,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Grid } from '@mui/material'
import ActionsDialog from '@src/components/ActionsDialog'
import { useYearOptions } from '@src/containers/ViewCreditApplicationPage/credit-hooks'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { Address } from '@src/types/AddressSchema'
import { ApplicantJob, JobSchema } from '@src/types/CreditApplicationSchema'
import { memo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AddressComponent from './AddressComponent'

interface Props {
  open: boolean
  title: string
  defaultValue: ApplicantJob
  onConfirm: (data: ApplicantJob) => void
  onCancel: () => void
  financingProgramId: EFinancingProgram
}

const EditJobDialog = ({ defaultValue, onConfirm, onCancel, open, title, financingProgramId }: Props) => {
  const { t } = useTranslation()

  const programConfig = programConfigByFinancingProgramId(financingProgramId)

  const { trigger, getValues, reset, control } = useForm<ApplicantJob>({
    mode: 'onBlur', // déclenche les validations Après que l'usager ait quitté le champ
    defaultValues: defaultValue,
    resolver: yupResolver(JobSchema()),
  })

  useEffect(() => {
    reset(defaultValue)
  }, [defaultValue, reset])

  const isAddressEmpty = (address: Address | null) => {
    return Boolean(address) && Object.values(address!).every((a) => a === null || a === undefined)
  }

  const handleSubmit = async () => {
    await trigger(['annualSalary', 'jobType', 'jobTitle', 'employerName', 'employerPhone', 'years', 'months']).then(
      (isValid) => {
        if (isValid) {
          const jobCopy = { ...getValues() }
          if (isAddressEmpty(jobCopy.address)) jobCopy.address = null
          onConfirm(jobCopy)
        }
      },
    )
  }

  const jobTypeOptions = useTranslatedOptionEnum('EJobType', getEJobTypeSelectableList(), 'enum')
  const yearsOptions = useYearOptions(15)

  return (
    <ActionsDialog title={title} open={open} onCancel={onCancel} onConfirm={handleSubmit}>
      <Grid container spacing={1} rowSpacing={2}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <ControlledNumberField
            name="annualSalary"
            control={control}
            label={t('editCreditApplication.income.job.annualSalary')}
            id="annualSalary"
            isMoney
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3,
          }}
        >
          <ControlledSelect
            name="jobType"
            control={control}
            label={t('editCreditApplication.income.job.jobType')}
            options={jobTypeOptions}
            disableEmptyOption
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ControlledTextField
            id="jobTitle"
            name="jobTitle"
            control={control}
            slotProps={{ htmlInput: { maxLength: 50 } }}
            label={t('editCreditApplication.income.job.jobTitle')}
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ControlledTextField
            id="employerName"
            name="employerName"
            control={control}
            slotProps={{ htmlInput: { maxLength: 100 } }}
            label={t('editCreditApplication.income.job.employerName')}
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 8,
            md: 4,
            lg: 5,
          }}
        >
          <ControlledPhoneField
            id="employerPhone"
            name="employerPhone"
            control={control}
            label={t('editCreditApplication.income.job.employerPhone')}
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 4,
            md: 2,
            lg: 1,
          }}
        >
          <ControlledTextField
            name="employerPhoneExt"
            control={control}
            id="employerPhoneExt"
            slotProps={{ input: { inputMode: 'numeric' }, htmlInput: { maxLength: 10 } }}
            label={t('editCreditApplication.income.job.employerPhoneExt')}
            fullWidth
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <ControlledSelect
            id="jobSinceYears"
            name="years"
            control={control}
            label={t('editCreditApplication.income.job.yearsAtThisJob')}
            fullWidth
            options={yearsOptions}
          />
        </Grid>
        {programConfig.useJobAddress && (
          <Grid size={12}>
            <AddressComponent formControl={control} isCommercial name="address" includeDuration={false} />
          </Grid>
        )}
      </Grid>
    </ActionsDialog>
  )
}

export default memo(EditJobDialog)
